import { DollarSign, Info, SlidersHorizontal } from "lucide-react";
import { useMemo, useState } from "react";
import { fmtCurrency } from "../../lib/format";
import { Slider } from "../ui/slider";

/**
 * Rough enterprise-value range. Two views: lender (conservative debt-capacity)
 * and buyer (full EV). Multiples are industry-typical rules of thumb — we
 * caveat heavily so this never gets confused with a formal valuation.
 *
 * For businesses with negative or zero EBITDA we render an explanatory stub
 * rather than the range bars.
 */
export default function ValuationRangeCard({
  valuation,
  currentEbitdaMargin,
  revenue,
  proActive = false,
}) {
  if (!valuation) return null;

  if (!valuation.available) {
    return (
      <div className="mb-6" data-testid="valuation-range-card">
        <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6 sm:p-8">
          <div className="flex items-center gap-2.5 mb-2">
            <DollarSign size={14} strokeWidth={1.5} className="text-[#C26D5C]" />
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#C26D5C] font-medium">
              Valuation range
            </p>
          </div>
          <p className="font-serif-display text-xl text-[#1F382E] tracking-tight leading-tight">
            Not enough EBITDA to estimate value yet.
          </p>
          <p className="text-[13px] text-[#5C665D] leading-relaxed mt-2 max-w-xl">
            {valuation.reason}
          </p>
        </div>
      </div>
    );
  }

  const { currency, lender_view: lender, buyer_view: buyer, total_debt, equity_low, equity_high, caveat } = valuation;

  return (
    <div className="mb-6" data-testid="valuation-range-card">
      <div className="bg-white border border-[#E5E0D8] rounded-2xl overflow-hidden">
        <div className="px-6 sm:px-8 py-5 border-b border-[#E5E0D8]">
          <div className="flex items-center gap-2.5">
            <DollarSign size={16} strokeWidth={1.75} className="text-[#C26D5C]" />
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#C26D5C] font-medium">
              Risk-adjusted valuation range
            </p>
          </div>
          <p className="font-serif-display text-xl sm:text-2xl text-[#1F382E] mt-1 tracking-tight leading-tight">
            What your business might be worth — to a lender vs a buyer.
          </p>
        </div>

        <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#E5E0D8]">
          {/* Lender view */}
          <div className="p-6 sm:p-8" data-testid="valuation-lender-view">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#5C665D] font-medium">
              Lender view
            </p>
            <p className="font-serif-display text-base text-[#1F382E] mt-1 mb-3">
              {lender.label}
            </p>
            <div className="flex items-baseline gap-2">
              <span className="font-serif-display text-3xl text-[#1F382E] tabular-nums" data-testid="valuation-lender-low">
                {fmtCurrency(lender.low, currency)}
              </span>
              <span className="text-[#5C665D] text-sm">–</span>
              <span className="font-serif-display text-3xl text-[#1F382E] tabular-nums" data-testid="valuation-lender-high">
                {fmtCurrency(lender.high, currency)}
              </span>
            </div>
            <p className="text-[12.5px] text-[#5C665D] leading-relaxed mt-3">{lender.blurb}</p>
          </div>

          {/* Buyer view */}
          <div className="p-6 sm:p-8 bg-[#C26D5C]/[0.04]" data-testid="valuation-buyer-view">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#C26D5C] font-medium">
              Buyer view
            </p>
            <p className="font-serif-display text-base text-[#1F382E] mt-1 mb-3">
              {buyer.label}
            </p>
            <div className="flex items-baseline gap-2">
              <span className="font-serif-display text-3xl text-[#5C665D] tabular-nums" data-testid="valuation-buyer-low">
                {fmtCurrency(buyer.low, currency)}
              </span>
              <span className="text-[#5C665D] text-sm">–</span>
              <span className="font-serif-display text-4xl text-[#C26D5C] tabular-nums" data-testid="valuation-buyer-mid">
                {fmtCurrency(buyer.mid, currency)}
              </span>
              <span className="text-[#5C665D] text-sm">–</span>
              <span className="font-serif-display text-3xl text-[#5C665D] tabular-nums" data-testid="valuation-buyer-high">
                {fmtCurrency(buyer.high, currency)}
              </span>
            </div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#5C665D] mt-1.5">
              Low · Mid · High
            </p>
            <p className="text-[12.5px] text-[#5C665D] leading-relaxed mt-3">{buyer.blurb}</p>

            {total_debt > 0 && (equity_low > 0 || equity_high > 0) && (
              <div className="mt-4 pt-4 border-t border-[#E5E0D8]/60">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#5C665D]">
                  Implied equity to owners
                </p>
                <p className="text-[14px] text-[#1F382E] tabular-nums mt-1" data-testid="valuation-equity-range">
                  {fmtCurrency(equity_low, currency)} – {fmtCurrency(equity_high, currency)}
                </p>
                <p className="text-[11px] text-[#5C665D] mt-1">
                  Buyer EV minus current debt of {fmtCurrency(total_debt, currency)}.
                </p>
              </div>
            )}
          </div>
        </div>

        <SensitivitySlider
          buyer={buyer}
          currency={currency}
          currentEbitdaMargin={currentEbitdaMargin}
          revenue={revenue}
          proActive={proActive}
        />

        <div className="bg-[#F7F5F0] border-t border-[#E5E0D8] px-6 sm:px-8 py-3.5 flex items-start gap-2">
          <Info size={12} strokeWidth={1.75} className="text-[#5C665D] mt-0.5 shrink-0" />
          <p className="text-[11px] text-[#5C665D] leading-snug" data-testid="valuation-caveat">
            {caveat}
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Pro-only "What if?" slider. Owners can nudge their EBITDA margin ±5 pts
 * around the current baseline and watch the valuation range recompute in real
 * time. Math: assume the range scales linearly with EBITDA (multiple stays
 * fixed), which is the accepted rule of thumb for sensitivity analysis at
 * this level. Not shown when we don't have the current margin + revenue
 * needed to recompute (e.g. negative-EBITDA cases).
 */
function SensitivitySlider({
  buyer,
  currency,
  currentEbitdaMargin,
  revenue,
  proActive,
}) {
  // Local state must be declared before any early return so React sees a
  // stable hook order across renders.
  const [pctDelta, setPctDelta] = useState(0);
  const currentEbitda =
    Number(currentEbitdaMargin) > 0 && Number(revenue) > 0
      ? (Number(currentEbitdaMargin) / 100) * Number(revenue)
      : 0;
  const projectedMargin = Number(currentEbitdaMargin) + pctDelta;
  const projectedEbitda =
    Number(revenue) > 0 ? (projectedMargin / 100) * Number(revenue) : 0;
  const scale = currentEbitda > 0 ? projectedEbitda / currentEbitda : 1;
  const projected = useMemo(
    () => ({
      low: Math.round(buyer.low * scale),
      mid: Math.round(buyer.mid * scale),
      high: Math.round(buyer.high * scale),
    }),
    [buyer, scale]
  );
  const dollarDelta = projected.mid - buyer.mid;

  // Only render for Pro users with the numbers we need to compute against.
  if (!proActive) return null;
  if (!currentEbitda || !buyer?.mid) return null;

  return (
    <div
      className="px-6 sm:px-8 py-5 border-t border-[#E5E0D8] bg-[#F7F5F0]/40"
      data-testid="valuation-sensitivity"
    >
      <div className="flex items-center gap-2 mb-1">
        <SlidersHorizontal
          size={13}
          strokeWidth={1.75}
          className="text-[#C26D5C]"
        />
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#C26D5C] font-medium">
          What if you improve your margin?
        </p>
      </div>
      <p className="text-[13px] text-[#5C665D] leading-relaxed">
        Drag the slider to see how a change to your EBITDA margin moves the
        buyer range. Baseline: {Number(currentEbitdaMargin).toFixed(1)}% EBITDA
        margin.
      </p>

      <div className="mt-5 flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <Slider
            value={[pctDelta]}
            onValueChange={(v) => setPctDelta(v[0])}
            min={-5}
            max={5}
            step={0.5}
            data-testid="valuation-sensitivity-slider"
          />
          <div className="flex justify-between mt-1.5 text-[10.5px] text-[#5C665D] tabular-nums">
            <span>-5 pts</span>
            <span>baseline</span>
            <span>+5 pts</span>
          </div>
        </div>
        <div
          className="text-right shrink-0"
          data-testid="valuation-sensitivity-summary"
        >
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#5C665D]">
            Projected margin
          </p>
          <p className="font-serif-display text-xl text-[#1F382E] tabular-nums">
            {projectedMargin.toFixed(1)}%
          </p>
        </div>
      </div>

      <div className="mt-5 flex items-baseline gap-2 flex-wrap">
        <span
          className="font-serif-display text-2xl sm:text-3xl text-[#1F382E] tabular-nums"
          data-testid="valuation-sensitivity-low"
        >
          {fmtCurrency(projected.low, currency)}
        </span>
        <span className="text-[#C26D5C]">—</span>
        <span
          className="font-serif-display text-3xl sm:text-4xl text-[#C26D5C] tabular-nums"
          data-testid="valuation-sensitivity-mid"
        >
          {fmtCurrency(projected.mid, currency)}
        </span>
        <span className="text-[#C26D5C]">—</span>
        <span
          className="font-serif-display text-2xl sm:text-3xl text-[#1F382E] tabular-nums"
          data-testid="valuation-sensitivity-high"
        >
          {fmtCurrency(projected.high, currency)}
        </span>
      </div>

      {pctDelta !== 0 && (
        <p
          className={`mt-2 text-[13px] font-medium tabular-nums ${dollarDelta > 0 ? "text-[#2C5C43]" : "text-[#A84632]"}`}
          data-testid="valuation-sensitivity-delta"
        >
          {dollarDelta > 0 ? "+" : ""}
          {fmtCurrency(dollarDelta, currency)} vs baseline midpoint
        </p>
      )}
    </div>
  );
}
